/**
 * HoverValueTooltip.tsx
 * صليب تصويب **ثابت في منتصف الشاشة** (لا يتبع المؤشّر) يعرض قيمة الطبقة الفعّالة عند
 * الإحداثي الجغرافي الواقع تحته حالياً — تماماً كأسلوب Zoom Earth: الصليب لا يتحرّك أبداً؛
 * ما يتحرّك هو الخريطة تحته، وكلما حرّكتَ الخريطة (سحب/تكبير) تُحدَّث القيمة المعروضة
 * لتطابق نقطة المركز الجديدة. سطر القيمة الأساسية (مثل 33°C)، وسطر الرياح (السرعة +
 * سهم الاتجاه + الجهة WSW) عند توفّر شبكة الرياح.
 *
 * يقرأ القيمة مباشرةً من نُسج GFS المحلّية عبر rasterSampler (بلا أي طلب API)،
 * فتظهر قيمة سلسة عبر استيفاء ثنائي الخطّية على الصورة.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useMapRef } from './MapContext';
import { useWeatherStore } from '../../store/weatherStore';
import { frameAtEpoch } from './hooks/useRadarFrames';
import { prefetchRadarSample, sampleRadarDbz } from '../../services/radarSampler';
import { dbzToMmH, formatMmH, intensityKey } from '../../config/radarUnits';
import { domainInView, type RadarDomain } from '../../services/mrmsRadarService';
import { rasterSampler } from '../../services/rasterSampler';
import {
    formatMainValue, formatPrecipAccumulated, precipitationNote, windDirToCardinal,
    PRECIP_ACCUMULATION_HOURS, isSnowAtTemp,
    type WindUnit, type PrecipUnit,
    type Translate,
} from './hoverValueFormat';
import { useTranslation } from '../../i18n/LanguageContext';
import type { ForecastGridType } from '../../config/weatherLayers';

interface HoverValueTooltipProps {
    /** الطبقة السلَّمية الفعّالة (للسطر الأساسي). */
    type: ForecastGridType | null;
    /** إطار الوقت الحالي (يحدّد نسيج الإطار المقروء). */
    timeIndex: number;
    /**
     * قراءة الرادار — تُمرَّر حين تكون طبقة الرادار فعّالة.
     *
     * منفصلة عن `type` لأن الرادار ليس `ForecastGridType`: قيمُه مرصودة لا متوقَّعة،
     * ولا تُقرأ من نُسج النموذج بل من صور الرادار نفسها بعكس التدرّج (radarSampler).
     */
    radar?: { domains: readonly RadarDomain[]; epoch: number } | null;
    /**
     * ساعة النموذج لقراءة الرياح — **قد تخالف `timeIndex`**.
     *
     * على محور الرادار يصير `timeIndex` فهرساً في إطارات متباعدة بدقيقتين، وتمريره
     * كساعة نموذج يقرأ رياح ساعاتٍ لا علاقة لها باللحظة المعروضة. تُشتقّ الساعة في
     * NewWeatherMap من الزمن المطلق (windHour) وتصل هنا جاهزة.
     */
    windTimeIndex?: number;
}

/**
 * هل الجهاز قادر على "المرور" (hover) أصلاً؟
 *
 * التلميح التابع للمؤشّر لا معنى له على شاشة لمس: لا مؤشّر يمرّ فوق شيء. ومع ذلك كان
 * يظهر على الجوال، لأن اللمسة تُولّد حدث mousemove واحداً فيعلَق التلميح في مكانه —
 * فتتراكم على الشاشة صناديق قيم: قراءة المركز، وتلميح لمسة عالق، وفقاعة النقر.
 *
 * الفحص بـ (hover: none) لا بعرض الشاشة: السؤال هو "هل يستطيع هذا الجهاز المرور؟"
 * لا "كم عرض النافذة؟". فلوح بلمس فقط يُعامَل كجوال ولو كان عريضاً، وشاشة سطح مكتب
 * ضيّقة تحتفظ بالتلميح لأن لديها فأرة.
 */
function canHover(): boolean {
    if (typeof window === 'undefined' || !window.matchMedia) return true;
    return window.matchMedia('(hover: hover)').matches;
}

interface WindInfo { speed: number; dir: number; cardinal: string }
interface CenterState { main: string; sub?: string; note: string | null; wind: WindInfo | null }
interface HoverPoint { x: number; y: number; lng: number; lat: number }

/**
 * يبني حالة العرض لنقطة (lat/lng) معيّنة عند طبقة/وقت معيّنين. الأمطار حالة خاصة: الرقم
 * الرئيسي ولون الخريطة (getAccumulatedFrameUrl في rasterSampler) كلاهما تراكم 3 ساعات
 * (أسلوب Windy) لا معدّلاً لحظياً — وتصنيف الشدّة (precipitationNote) يُبنى على نفس
 * التراكم أيضاً كي يطابق اللون المرسوم فعلياً تحت المؤشّر، لا معدّلاً لحظياً منفصلاً عنه.
 * يُرجع null إن لم تصل بيانات إحدى الساعات اللازمة بعد (استمرار نفس اتفاقية rasterSampler:
 * التحميل يُطلَق ضمنياً ويُعاد المحاولة في الإطار التالي).
 */
function buildRadarState(
    radar: { domains: readonly RadarDomain[]; epoch: number },
    windTi: number, lat: number, lng: number, t: Translate,
): CenterState | null {
    const dbz = sampleRadarDbz(radar.domains, (d) => frameAtEpoch(d, radar.epoch), lat, lng);
    if (dbz === null || !Number.isFinite(dbz)) return null;

    /*
     * السطر الأساسي **mm/h لا dBZ**، والوصف بالكلمات تحته.
     *
     * ‏dBZ وحدةٌ صحيحة لا يفهمها غير المختصّ: «47 dBZ» لا تقول للقارئ هل يخرج بمظلّة.
     * فنعرض ما تعرضه Windy (معدّلاً بـmm/h) وما تعرضه Zoom Earth (وصفاً بالكلام) معاً،
     * ونُبقي dBZ سطراً فرعياً لمن يريد الرقم الأصلي — فلا نخسر دقّةً ولا وضوحاً.
     */
    const mmh = dbzToMmH(dbz);
    const wp = rasterSampler.sampleWind(windTi, lat, lng);
    const wind = wp && wp.speed >= 0.5
        ? { speed: wp.speed, dir: wp.direction, cardinal: windDirToCardinal(wp.direction, t) }
        : null;
    return {
        main: `${formatMmH(mmh)} mm/h`,
        sub: `${dbz.toFixed(0)} dBZ`,
        note: t(intensityKey(dbz)),
        wind,
    };
}

function buildCenterState(
    type: ForecastGridType, ti: number, lat: number, lng: number,
    fahrenheit: boolean, windUnit: WindUnit, precipUnit: PrecipUnit, t: Translate,
): CenterState | null {
    if (type === 'precipitation') {
        const accumulated = rasterSampler.sampleAccumulated(type, ti, lat, lng, PRECIP_ACCUMULATION_HOURS);
        if (accumulated === null || !Number.isFinite(accumulated)) return null;
        // نوع الهطول من حرارة النقطة نفسها: تحت ~1° يصير الرقم ثلجاً لا مطراً، فتتغيّر
        // التسمية («ثلوج خفيفة» بدل «أمطار خفيفة») والعنوان الفرعي معاً. الحرارة قد تكون
        // null إن لم يصل راستر الحرارة بعد — عندها نبقى على تسمية المطر بدل تخمين ثلج.
        const tempC = rasterSampler.sampleScalar('temperature', ti, lat, lng);
        const snow = isSnowAtTemp(tempC);
        const { main, sub } = formatPrecipAccumulated(accumulated, t, precipUnit, snow);
        return { main, sub, note: precipitationNote(type, accumulated, t, snow), wind: null };
    }
    const value = rasterSampler.sampleScalar(type, ti, lat, lng);
    if (value === null || !Number.isFinite(value)) return null;
    let wind: WindInfo | null = null;
    if (type === 'wind') {
        const wp = rasterSampler.sampleWind(ti, lat, lng);
        if (wp && wp.speed >= 0.5) wind = { speed: wp.speed, dir: wp.direction, cardinal: windDirToCardinal(wp.direction, t) };
    }
    return { main: formatMainValue(type, value, fahrenheit, t, windUnit, precipUnit), note: precipitationNote(type, value, t), wind };
}

export function HoverValueTooltip({ type, timeIndex, radar, windTimeIndex }: HoverValueTooltipProps) {
    const { t } = useTranslation();
    const mapRef = useMapRef();
    const fahrenheit = useWeatherStore((s) => s.units.temperature === 'fahrenheit');
    const windUnit = useWeatherStore((s) => s.units.wind);
    const precipUnit = useWeatherStore((s) => s.units.precipitation);
    // حدود الشاشة: الإحماء أدناه كان يشمل نطاقات الرادار كلّها (راجع domainInView).
    const mapBounds = useWeatherStore((s) => s.mapBounds);
    const playing = useWeatherStore((s) => s.isPlaying);
    // أثناء القياس تصمت شارة القيمة كلياً: هي مثبّتة أعلى الشاشة حيث تجلس بطاقة
    // القياس وصليب التصويب يتبع المؤشّر نفسه الذي يرسم به المستخدم — فتغطّي البطاقة
    // وتزاحم النقاط معاً. والمستخدم في هذا الوضع يقرأ المسافة لا سرعة الرياح.
    const measuring = useWeatherStore((s) => s.measureMode !== 'none');
    const [center, setCenter] = useState<CenterState | null>(null);
    const [mapCenter, setMapCenter] = useState<CenterState | null>(null);
    const [hoverPoint, setHoverPoint] = useState<HoverPoint | null>(null);

    // نحتفظ بأحدث البيانات في ref حتى لا نعيد ربط المستمعات مع كل تغيّر بيانات.
    const dataRef = useRef<{ type: ForecastGridType | null; timeIndex: number; fahrenheit: boolean; windUnit: typeof windUnit; precipUnit: typeof precipUnit; t: Translate; radar: typeof radar; windTimeIndex: number }>(
        { type, timeIndex, fahrenheit, windUnit, precipUnit, t, radar, windTimeIndex: windTimeIndex ?? timeIndex });
    dataRef.current = { type, timeIndex, fahrenheit, windUnit, precipUnit, t, radar, windTimeIndex: windTimeIndex ?? timeIndex };

    /**
     * مُرسِلٌ واحد لكل مواضع النداء الثلاثة.
     *
     * الرادار **يسبق** الطبقة السلَّمية: حين يكون فعّالاً فهو ما يراه المستخدم تحت
     * المؤشّر، وقراءةُ حقلٍ آخر تحته تقول رقماً عن شيء غير معروض.
     */
    const build = useCallback((
        layerType: ForecastGridType | null, ti: number, lat: number, lng: number,
        f: boolean, wu: WindUnit, pu: PrecipUnit, translate: Translate,
        rad: typeof radar, windTi: number,
    ): CenterState | null => {
        if (rad && rad.domains.length) return buildRadarState(rad, windTi, lat, lng, translate);
        if (!layerType) return null;
        return buildCenterState(layerType, ti, lat, lng, f, wu, pu, translate);
    }, []);

    // نحمّل نسيج الطبقة الفعّالة + الرياح للإطار الحالي مسبقاً ليجهز القراءة فوراً.
    useEffect(() => {
        // ── لا إحماء لعيّنة الرادار أثناء التشغيل ────────────────────────────
        // هذا الإحماء يفكّ ترميز إطارٍ **كامل** (16.8 ميغابكسل) لكل نطاق مرئي، ومفتاحه
        // `timeIndex` — أي عند كل خطوة. فكان يُضاعف كلفة الحركة التي نعالجها، لأجل
        // قراءةٍ لا تقع أصلاً: لا أحد يقرأ رقم dBZ تحت مؤشّره والحلقة تجري. يعود
        // الإحماء فور التوقّف، وهناك تُقرأ القيم فعلاً.
        if (!playing && radar && radar.domains.length) {
            /*
             * ── النطاقات المرئية وحدها ────────────────────────────────────────
             *
             * كان هذا السطر يفكّ ترميز إطار **كل** نطاق (عشرون نطاقاً: أمريكا وألاسكا
             * وهاواي وغوام والكاريبي وأوروبا وكندا وإيطاليا) عند كل خطوة على الشريط،
             * لأن مفتاح المؤثّر `timeIndex`. فبقي التشغيل يجرّ ميغابايتات حتى بعد أن
             * قصصنا النطاقات في طبقة العرض نفسها — وهو ما قِيس حيّاً فوق إيطاليا في
             * 2026-09-19: إطارات ألاسكا وهاواي وكانساس لا تزال تُطلب أثناء الحلقة.
             *
             * والتلميح لا يقرأ إلا النطاق الواقع تحت المؤشّر، والمؤشّر على الشاشة
             * بحكم التعريف — فما خرج عن الشاشة لا يُقرأ منه شيء أصلاً.
             */
            /*
             * **وسط الشاشة لا الشاشة كلّها.** فكّ إطارٍ واحد يكلّف عشرات المللي حتى
             * من المصغَّرة، وعند التصغير الكامل تقع عشرون نطاقاً داخل الشاشة — فيصير
             * التحميل المسبق وحده نصف ثانية من الحجز. والمؤشّر لا يقرأ إلا نطاقاً
             * واحداً، والأرجح أنه الذي يغطّي وسط الخريطة. وما عداه يُفكّ عند أوّل
             * تحويمٍ فوقه فعلاً (‏sampleRadarDbz يفكّ عند الطلب).
             */
            const cLat = mapBounds ? (mapBounds.north + mapBounds.south) / 2 : null;
            const cLon = mapBounds ? (mapBounds.east + mapBounds.west) / 2 : null;
            const atCenter = cLat === null || cLon === null ? [] : radar.domains.filter((d) =>
                cLat <= d.bounds.north && cLat >= d.bounds.south
                && cLon >= d.bounds.west && cLon <= d.bounds.east);
            prefetchRadarSample(
                (atCenter.length ? atCenter : radar.domains.filter(
                    (d) => domainInView(d.bounds, mapBounds))).slice(0, 2),
                (d) => frameAtEpoch(d, radar.epoch),
            );
        }
        if (type) rasterSampler.prefetch(type, timeIndex);
        rasterSampler.prefetch('wind', windTimeIndex ?? timeIndex);
        // طبقة الأمطار تحتاج الحرارة أيضاً لتمييز الثلج عن المطر في التسمية.
        if (type === 'precipitation') rasterSampler.prefetch('temperature', timeIndex);
    }, [type, timeIndex, mapBounds, playing]);

    useEffect(() => {
        let map: ReturnType<NonNullable<typeof mapRef.current>['getMap']> | null = null;
        let pollId: number | undefined;
        let rafId: number | null = null;
        let onMouseMove: ((event: { point: { x: number; y: number }; lngLat: { lng: number; lat: number } }) => void) | null = null;
        let onMouseLeave: (() => void) | null = null;

        const hoverRef = { current: null as HoverPoint | null };

        const recompute = () => {
            rafId = null;
            if (!map) return;
            const { type: layerType, timeIndex: ti, fahrenheit: f, windUnit: wu, precipUnit: pu, t: translate, radar: rad, windTimeIndex: wti } = dataRef.current;
            if (!layerType) { setCenter(null); return; }
            const p = hoverRef.current;
            if (!p) { setCenter(null); return; }
            setCenter(build(layerType, ti, p.lat, p.lng, f, wu, pu, translate, rad, wti));
        };

        const scheduleRecompute = () => { if (rafId === null) rafId = window.requestAnimationFrame(recompute); };

        // الخريطة قد لا تكون جاهزة عند أول تركيب (هذا المكوّن خارج <Map>) — ننتظرها.
        const attach = () => {
            const wrap = mapRef.current;
            if (!wrap) return false;
            map = wrap.getMap();
            // على أجهزة اللمس لا نربط مستمع المرور إطلاقاً: لا تلميح متتبّع، ولا عمل
            // إضافي عند كل لمسة. قراءة المركز (صليب التصويب) تبقى كما هي.
            if (!canHover()) return true;
            onMouseMove = (event: { point: { x: number; y: number }; lngLat: { lng: number; lat: number } }) => {
                hoverRef.current = { x: event.point.x, y: event.point.y, lng: event.lngLat.lng, lat: event.lngLat.lat };
                setHoverPoint(hoverRef.current);
                scheduleRecompute();
            };
            onMouseLeave = () => { hoverRef.current = null; setHoverPoint(null); setCenter(null); };
            map.on('mousemove', onMouseMove);
            map.getContainer().addEventListener('mouseleave', onMouseLeave);
            return true;
        };
        if (!attach()) {
            pollId = window.setInterval(() => { if (attach()) window.clearInterval(pollId); }, 200);
        }

        return () => {
            if (pollId !== undefined) window.clearInterval(pollId);
            if (map) {
                if (onMouseMove) map.off('mousemove', onMouseMove as never);
                if (onMouseLeave) map.getContainer().removeEventListener('mouseleave', onMouseLeave);
            }
            if (rafId !== null) window.cancelAnimationFrame(rafId);
        };
    }, [mapRef]);

    // قراءة مستقلة لمركز الخريطة الذي تقع تحته علامة التصويب الثابتة.
    useEffect(() => {
        let map: ReturnType<NonNullable<typeof mapRef.current>['getMap']> | null = null;
        let timer: number | undefined;
        let raf = 0;
        /*
         * حدث `move` يُطلَق في كل إطار أثناء السحب. القراءة المباشرة عنده كانت تستدعي
         * setState ستّين مرّة في الثانية بكائنٍ جديد في كل مرّة، فتُعاد تهيئة React كاملةً
         * مع كل إطار سحب — وهذا سببٌ مباشر لتثاقل السحب واستجابة الأزرار.
         *
         * الآن: قراءة واحدة كحدّ أقصى لكل إطار (rAF)، ولا تحديث للحالة إلا إذا تغيّر
         * **النصّ المعروض** فعلاً (القيمة مقرَّبة، فلا تتغيّر مع كل بكسل). النتيجة على
         * الشاشة مطابقة تماماً، وعدد إعادات الرسم يهبط إلى ما يراه المستخدم فقط.
         */
        const sameWind = (a: WindInfo | null, b: WindInfo | null) =>
            (a === b) || (!!a && !!b && a.speed === b.speed && a.dir === b.dir && a.cardinal === b.cardinal);
        const same = (a: CenterState | null, b: CenterState | null) =>
            (a === b) || (!!a && !!b && a.main === b.main && a.sub === b.sub && a.note === b.note && sameWind(a.wind, b.wind));

        const doRead = () => {
            raf = 0;
            if (!map || !type) { setMapCenter(null); return; }
            const c = map.getCenter();
            const next = build(type, timeIndex, c.lat, c.lng, fahrenheit, windUnit, precipUnit, t, radar, windTimeIndex ?? timeIndex);
            setMapCenter(prev => (same(prev, next) ? prev : next));
        };
        const read = () => { if (!raf) raf = window.requestAnimationFrame(doRead); };
        const attach = () => {
            const wrap = mapRef.current;
            if (!wrap) return false;
            map = wrap.getMap();
            map.on('move', read);
            doRead();
            return true;
        };
        if (!attach()) timer = window.setInterval(() => { if (attach() && timer) window.clearInterval(timer); }, 200);
        return () => {
            if (timer) window.clearInterval(timer);
            if (raf) window.cancelAnimationFrame(raf);
            if (map) map.off('move', read);
        };
    }, [mapRef, type, timeIndex, fahrenheit, windUnit, precipUnit, t]);

    // تغيّر الطبقة/الوقت لا يحرّك الخريطة، فلا يُطلق 'move' — نعيد الحساب يدوياً كي تتحدّث
    // القيمة فوراً عند تبديل الطبقة أو تحريك الشريط الزمني بلا سحب الخريطة.
    useEffect(() => {
        const map = mapRef.current?.getMap();
        if (!map) return;
        // لا نعيد القراءة هنا إلا عندما يوجد hover فعلي؛ موضع القراءة هو المؤشر الآن.
        if (!hoverPoint) return;
        if (!type) { setCenter(null); return; }
        const c = map.unproject([hoverPoint.x, hoverPoint.y]);
        setCenter(build(type, timeIndex, c.lat, c.lng, fahrenheit, windUnit, precipUnit, t, radar, windTimeIndex ?? timeIndex));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [type, timeIndex, fahrenheit, windUnit, precipUnit, hoverPoint, t]);

    // علامة التصويب قابلة للإخفاء من لوحة التراكبات. التلميح نفسه يبقى: القراءة عند
    // مركز الشاشة معلومة، والعلامة مجرّد دليلٍ بصري عليها.
    const crosshairOn = useWeatherStore((st) => st.crosshairEnabled);
    /*
     * ختم الوقت يجلس في أعلى منتصف الشاشة، وقراءة التصويب كذلك — فيتراكبان حين
     * يُشغَّل الختم (رُصد 2026-09-17: «لا هطول» فوق «الخميس 17 سبتمبر»).
     *
     * والإزاحة هنا لا هناك: الختم عنصرٌ ثابت الموضع يُلتقط في صورة الشاشة، وقراءة
     * التصويب تتبع ما تحت المؤشّر فهي الأولى بالتنحّي. وإخفاؤها كان سيُضيّع القيمة
     * التي فتح المستخدم التصويب لأجلها أصلاً — فالتنازل عن الموضع أرخص من التنازل
     * عن المعلومة.
     */
    const stampOn = useWeatherStore((st) => st.timestampBadgeEnabled);

    if (measuring) return null;

    return (
        <>
            {/* صليب تصويب ثابت في منتصف الشاشة (أسلوب Zoom Earth) — لا يتحرّك مطلقاً؛
                فجوة صغيرة في المنتصف بدل تقاطع مصمت. */}
            {crosshairOn && (
                <div className="hover-value-crosshair fixed left-1/2 top-1/2 z-[750] w-0 h-0 pointer-events-none select-none" aria-hidden>
                    <span className="absolute bg-white shadow-[0_0_0_1px_rgba(0,0,0,0.55)] rounded-[1px] w-1.5 h-[1.5px] top-[-0.75px] left-[-8px]" />
                    <span className="absolute bg-white shadow-[0_0_0_1px_rgba(0,0,0,0.55)] rounded-[1px] w-1.5 h-[1.5px] top-[-0.75px] left-0.5" />
                    <span className="absolute bg-white shadow-[0_0_0_1px_rgba(0,0,0,0.55)] rounded-[1px] w-[1.5px] h-1.5 left-[-0.75px] top-[-8px]" />
                    <span className="absolute bg-white shadow-[0_0_0_1px_rgba(0,0,0,0.55)] rounded-[1px] w-[1.5px] h-1.5 left-[-0.75px] top-0.5" />
                </div>
            )}
            {mapCenter && (
                <div className="hover-value-tooltip fixed left-1/2 top-1/2 z-[790] flex items-center gap-2 px-[11px] py-1 rounded-full bg-[rgba(22,27,34,0.92)] light:bg-[rgba(255,255,255,0.94)] text-[#f3f6fa] light:text-[#16202c] text-[13px] font-bold leading-[1.4] tracking-[0.2px] whitespace-nowrap border border-white/[0.14] light:border-black/10 shadow-[0_2px_12px_rgba(0,0,0,0.35)] light:shadow-[0_2px_12px_rgba(0,0,0,0.18)] backdrop-blur-[10px] pointer-events-none select-none [direction:ltr] [&_.hv-main]:text-sm [&_.hv-main]:font-extrabold [&_.hv-note]:text-xs [&_.hv-note]:font-semibold [&_.hv-note]:opacity-[0.82] [&_.hv-note+.hv-main]:ps-2 [&_.hv-note+.hv-main]:border-s [&_.hv-note+.hv-main]:border-white/[0.22] [&_.hv-sub]:text-[11px] [&_.hv-sub]:font-medium [&_.hv-sub]:opacity-65 [&_.hv-sub]:whitespace-nowrap [&_.hv-wind]:inline-flex [&_.hv-wind]:items-center [&_.hv-wind]:gap-[5px] [&_.hv-wind]:text-xs [&_.hv-wind]:font-semibold [&_.hv-wind]:opacity-90 [&_.hv-sep]:w-px [&_.hv-sep]:h-[13px] [&_.hv-sep]:bg-current [&_.hv-sep]:opacity-[0.28] [&_.hv-arrow]:inline-block [&_.hv-arrow]:text-[13px] [&_.hv-arrow]:leading-none [&_.hv-arrow]:[transform-origin:center] top-[clamp(5px,5vh,20px)] translate-x-[-50%] translate-y-0 max-w-[min(92vw,360px)] max-[640px]:top-[clamp(5px,14vh,100px)] max-[640px]:max-w-[calc(100vw-32px)]"
                    // تقف تحت حافّة الختم المقيسة بثماني بكسلات — لا بإزاحة ثابتة:
                    // الختم نفسه يتحرّك بشريط مفتاح الألوان وبعدد أسطره، فالإزاحة
                    // الثابتة تركت فجوةً واسعة هنا وكانت ستُلاصق في حالة أخرى.
                    // ولا حاجة لقيمة الصنف الأصلية: الختم دائماً أعلى من القراءة،
                    // فحافّته وحدها تكفي لتحديد الموضع.
                    style={stampOn ? { top: 'calc(var(--stamp-bottom, 100px) + 8px)' } : undefined}
                    aria-hidden>
                    {mapCenter.note && <span className="hv-note">{mapCenter.note}</span>}
                    <span className="hv-main">{mapCenter.main}</span>
                    {mapCenter.sub && <span className="hv-sub">{mapCenter.sub}</span>}
                    {mapCenter.wind && <span className="hv-wind"><span className="hv-sep" /><span className="hv-arrow" style={{ transform: `rotate(${mapCenter.wind.dir + 180}deg)` }}>↑</span>{mapCenter.wind.cardinal}</span>}
                </div>
            )}
            {center && hoverPoint && canHover() && (
                <div className="hover-value-tooltip fixed left-1/2 top-1/2 z-[790] flex items-center gap-2 px-[11px] py-1 rounded-full bg-[rgba(22,27,34,0.92)] light:bg-[rgba(255,255,255,0.94)] text-[#f3f6fa] light:text-[#16202c] text-[13px] font-bold leading-[1.4] tracking-[0.2px] whitespace-nowrap border border-white/[0.14] light:border-black/10 shadow-[0_2px_12px_rgba(0,0,0,0.35)] light:shadow-[0_2px_12px_rgba(0,0,0,0.18)] backdrop-blur-[10px] pointer-events-none select-none [direction:ltr] [&_.hv-main]:text-sm [&_.hv-main]:font-extrabold [&_.hv-note]:text-xs [&_.hv-note]:font-semibold [&_.hv-note]:opacity-[0.82] [&_.hv-note+.hv-main]:ps-2 [&_.hv-note+.hv-main]:border-s [&_.hv-note+.hv-main]:border-white/[0.22] [&_.hv-sub]:text-[11px] [&_.hv-sub]:font-medium [&_.hv-sub]:opacity-65 [&_.hv-sub]:whitespace-nowrap [&_.hv-wind]:inline-flex [&_.hv-wind]:items-center [&_.hv-wind]:gap-[5px] [&_.hv-wind]:text-xs [&_.hv-wind]:font-semibold [&_.hv-wind]:opacity-90 [&_.hv-sep]:w-px [&_.hv-sep]:h-[13px] [&_.hv-sep]:bg-current [&_.hv-sep]:opacity-[0.28] [&_.hv-arrow]:inline-block [&_.hv-arrow]:text-[13px] [&_.hv-arrow]:leading-none [&_.hv-arrow]:[transform-origin:center] translate-x-[-50%] translate-y-[calc(-100%-18px)]" style={{ left: hoverPoint.x, top: hoverPoint.y }} aria-hidden>
                    {center.note && <span className="hv-note">{center.note}</span>}
                    <span className="hv-main">{center.main}</span>
                    {center.sub && <span className="hv-sub">{center.sub}</span>}
                    {center.wind && (
                        <span className="hv-wind">
                            <span className="hv-sep" />
                            <span className="hv-arrow" style={{ transform: `rotate(${center.wind.dir + 180}deg)` }}>↑</span>
                            {center.wind.cardinal}
                        </span>
                    )}
                </div>
            )}
        </>
    );
}
